import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { getNiuHealthBaseUrl } from 'config/app.config';

/**
 * Custom hook for managing patient popup flow sequence in NIU Social
 * 
 * Simplified access control - only checks subscription status:
 * 1. Subscription popup - checks !patientDetails.data?.has_active_subscription
 * 
 * Profile completion and identification approval checks have been removed.
 * 
 * @param {Object} patientDetails - Patient details from NIU Health API
 * @param {Array} dependencies - Additional dependencies for useEffect
 * @param {Object} options - Configuration options
 * @param {boolean} options.useServiceMessage - Use service-specific subscription message
 */
const usePatientPopupFlow = (patientDetails, dependencies = [], options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { useServiceMessage = false } = options;
  
  // Only load translations when we actually need them (when patientDetails exists)
  // This prevents suspension issues during initial load
  const { t: patient_virtual_visit, ready: virtualVisitReady } = useTranslation('patient_virtual_visit', { 
    useSuspense: false 
  });
  const { t: patient_subscription, ready: subscriptionReady } = useTranslation('patient_subscription', { 
    useSuspense: false 
  });

  // NIU Health base URL for redirections
  const niuHealthBaseUrl = getNiuHealthBaseUrl();

  // Helper function to check if user is already on a restricted page
  const isOnRestrictedPage = () => {
    const restrictedPaths = ['/settings', '/submit', '/leaderboard'];
    return restrictedPaths.includes(location.pathname);
  };

  // Helper function to check if user is on homepage (no popups should show)
  const isOnHomePage = () => {
    return location.pathname === '/home' || location.pathname === '/';
  };

  // Popup configuration with NIU Health redirection URLs
  // Only subscription check - profile completion and identification approval checks removed
  const popupConfig = [
    {
      id: 'subscription',
      condition: (data) => !data?.has_active_subscription,
      // Only show on restricted pages, not on homepage
      shouldShow: (data) => !data?.has_active_subscription && isOnRestrictedPage() && !isOnHomePage(),
      popup: {
        html: 'Subscribe now to unlock the Virtual Visit feature and explore more!',
        confirmButtonText: 'Subscribe now',
      },
      navigation: `${niuHealthBaseUrl}/patient/subscription` // Redirect to NIU Health
    }
  ];

  useEffect(() => {
    // Only proceed if we have patient details, translations are ready, and user is on a restricted page
    if (patientDetails?.status && patientDetails.data && virtualVisitReady && subscriptionReady && 
        isOnRestrictedPage() && !isOnHomePage()) {
      
      // Find the first popup that should be shown based on the sequence
      const popupToShow = popupConfig.find(config =>
        config.shouldShow(patientDetails.data)
      );

      if (popupToShow) {
        Swal.fire({
          html: popupToShow.popup.html,
          confirmButtonText: popupToShow.popup.confirmButtonText,
          showConfirmButton: true,
          showCancelButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          // Remove default icon to match NIU Health design
          icon: undefined,
          // NIU Health exact styling match
          customClass: {
            container: 'niu-health-popup',
            popup: 'niu-health-popup',
            confirmButton: 'niu-health-button-green'
          },
          buttonsStyling: false,
          // Match NIU Health exact popup configuration
          width: 512,
          padding: '1.25em',
          background: '#ffffff',
          backdrop: 'rgba(0,0,0,0.4)',
        }).then((result) => {
          if (result.isConfirmed) {
            // Redirect to NIU Health for subscription/profile/verification actions
            window.location.href = popupToShow.navigation;
          }
        });
      }
    }
  }, [patientDetails, location.pathname, virtualVisitReady, subscriptionReady, ...dependencies]);

  // Return utility functions if needed
  return {
    // Helper function to check if any popup should be shown (considering page context)
    shouldShowPopup: () => {
      if (!patientDetails?.status || !patientDetails.data || !isOnRestrictedPage() || isOnHomePage()) {
        return false;
      }
      return popupConfig.some(config => config.shouldShow(patientDetails.data));
    },

    // Helper function to get the next popup that should be shown (considering page context)
    getNextPopup: () => {
      if (!patientDetails?.status || !patientDetails.data || !isOnRestrictedPage() || isOnHomePage()) {
        return null;
      }
      return popupConfig.find(config => config.shouldShow(patientDetails.data));
    },

    // Helper function to check completion status for debugging
    getCompletionStatus: () => {
      if (!patientDetails?.status || !patientDetails.data) return null;
      return {
        hasActiveSubscription: !!patientDetails.data.has_active_subscription,
        // Profile completion and identification approval checks removed
        currentPage: location.pathname,
        isRestrictedPage: isOnRestrictedPage(),
        isHomePage: isOnHomePage(),
        shouldShowAnyPopup: popupConfig.some(config => config.shouldShow(patientDetails.data))
      };
    }
  };
};

export default usePatientPopupFlow;